// updated version 1.0.2 - Added Advanced Analysis Modal (Non-breaking)

import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFirmMode } from '../contexts/FirmModeContext';
import FirmCaseContextBar from '../components/FirmCaseContextBar';
import { useIsMobile } from '../hooks/useIsMobile';
import './UnifiedPage.css';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';
import { buildFirmCaseContextPayload } from '../utils/firmCaseContextPayload';
import { formatMessageText } from '../utils/textUtils';
import { trackInteraction } from '../utils/sessionUtils';
import { Copy, ThumbsUp, ThumbsDown, Share } from 'lucide-react';
import UpgradeModal from '../components/UpgradeModal';
import InlinePaywallPreviewCard from '../components/InlinePaywallPreviewCard';
import { buildPaywallPreviewParts } from '../utils/paywallPreview';
import {
  extractAssistantReplyText,
  extractWebhookActions,
  extractWebhookBilling,
} from '../utils/webhookResponseParse';

const ChatPage = () => {
  const { t, isRTL } = useLanguage();
  const { user } = useAuth();
  const { isFirmMode, activeFirmCase, clearActiveFirmCase } = useFirmMode();
  const navigate = useNavigate();

  // Sidebar
  const sidebarRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const isMobile = useIsMobile();

  // ✅ NEW (Modal state) - ADDED ONLY
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const [selectedMessageText, setSelectedMessageText] = useState('');

  // 🔔 highlight advanced analysis button
const [highlightAdvancedBtn, setHighlightAdvancedBtn] = useState(false);


  // 🔔 Prediction suggestion toast
const [showPredictionToast, setShowPredictionToast] = useState(false);


  // Conversations + Messages
  const [conversations, setConversations] = useState(() => {
    const saved = localStorage.getItem("chat_conversations");
    return saved ? JSON.parse(saved) : [];
  });
  const [currentConversationId, setCurrentConversationId] = useState(() => {
    const saved = localStorage.getItem("chat_conversations");
    const parsed = saved ? JSON.parse(saved) : [];
    return parsed.length ? parsed[0].id : null;
  });
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem("chat_conversations");
    const parsed = saved ? JSON.parse(saved) : [];
    return parsed.length ? (parsed[0].messages || []) : [];
  });

  const [isLoading, setIsLoading] = useState(false);
  const parseBillingHeader = (response) => {
    try {
      const raw = response?.headers?.get('x-ydim-billing');
      if (!raw) return null;
      return JSON.parse(decodeURIComponent(raw));
    } catch {
      return null;
    }
  };

  const [billingUi, setBillingUi] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // Persist conversations
  useEffect(() => {
    localStorage.setItem("chat_conversations", JSON.stringify(conversations));
  }, [conversations]);

  // Helpers - Safe date formatting
  const safeDate = (value) => {
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    if (value) {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  };

  const formatDate = (dateValue) => {
    const date = safeDate(dateValue);
    if (!date) return '';
    try {
      return date.toLocaleDateString();
    } catch (error) {
      console.warn('Error formatting date:', error);
      return '';
    }
  };

  const formatTime = (dateValue) => {
    const date = safeDate(dateValue);
    if (!date) return '--:--';
    try {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      console.warn('Error formatting time:', error);
      return '--:--';
    }
  };

  // Generate / store userId
  const getUserId = () => {
    let uid = localStorage.getItem("chat_chatUserId");
    if (!uid) {
      uid = "user-" + Math.random().toString(36).substr(2, 9);
      localStorage.setItem("chat_chatUserId", uid);
    }
    return uid;
  };
  const userId = getUserId();

  // Generate / store sessionId (for n8n Memo Memory node)
  const getSessionId = () => {
    let sid = localStorage.getItem("chat_sessionId");
    if (!sid) {
      // Use crypto.randomUUID() if available, otherwise fallback to custom UUID
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        sid = crypto.randomUUID();
      } else {
        sid = 'session-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now().toString(36);
      }
      localStorage.setItem("chat_sessionId", sid);
    }
    return sid;
  };
  const sessionId = getSessionId();

  // Input / Files
  const [inputValue, setInputValue] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]); // [{file, previewUrl, kind:'image'|'file'}]
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const chatMessagesRef = useRef(null);

  // Scroll behavior state
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Check if user is at bottom of chat
  const checkIfAtBottom = () => {
    if (!chatMessagesRef.current) return false;
    const { scrollTop, scrollHeight, clientHeight } = chatMessagesRef.current;
    const threshold = 100; // Allow 100px threshold for "at bottom"
    return scrollHeight - scrollTop - clientHeight < threshold;
  };

  // Handle scroll events
  const handleScroll = () => {
    const atBottom = checkIfAtBottom();
    setIsAtBottom(atBottom);
    setShowScrollButton(!atBottom);

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Set user scrolling state
    setIsUserScrolling(true);

    // Reset user scrolling state after 150ms of no scrolling
    scrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false);
    }, 150);
  };

  // Auto-scroll to bottom (only if user hasn't scrolled up)
  const autoScrollToBottom = () => {
    if (isAtBottom && !isUserScrolling) {
      scrollToBottom();
    }
  };

  // Manual scroll to bottom (when user clicks button)
  const manualScrollToBottom = () => {
    scrollToBottom();
    setIsAtBottom(true);
    setShowScrollButton(false);
    setIsUserScrolling(false);
  };

  // Scroll effect - only auto-scroll if at bottom
  useEffect(() => {
    autoScrollToBottom();
  }, [messages, isAtBottom, isUserScrolling]);

  // Immediate scroll on new messages (ensures scroll happens even if state hasn't updated)
  useEffect(() => {
    if (messages.length > 0) {
      // Check if at bottom before scrolling
      const atBottom = checkIfAtBottom();
      if (atBottom && !isUserScrolling) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    }
  }, [messages.length]);

  // Add scroll event listener
  useEffect(() => {
    const chatContainer = chatMessagesRef.current;
    if (chatContainer) {
      chatContainer.addEventListener('scroll', handleScroll);
      return () => {
        chatContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Handle sidebar state based on mobile detection
  useEffect(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    } else {
      setIsSidebarOpen(true);
    }
  }, [isMobile]);

  // Handle overlay click to close sidebar on mobile
  const handleOverlayClick = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [inputValue]);

  // RTL/LTR detector
  const detectDirection = (text) => /[\u0600-\u06FF]/.test(text) ? 'rtl' : 'ltr';

  // Create new conversation
  const createNewConversation = () => {
    const newConv = {
      id: Date.now(),
      title: `Conversation ${conversations.length + 1}`,
      createdAt: new Date().toISOString(),
      messages: []
    };
    const updated = [newConv, ...conversations];
    setConversations(updated);
    setCurrentConversationId(newConv.id);
    setMessages([]);
  };

  // Load conversation
  const loadConversation = (id) => {
    setCurrentConversationId(id);
    const conv = conversations.find(c => c.id === id);
    setMessages(conv ? conv.messages : []);
  };

  // Delete conversation
  const deleteConversation = (id) => {
    const updated = conversations.filter((c) => c.id !== id);
    setConversations(updated);
    if (currentConversationId === id) {
      setCurrentConversationId(updated.length ? updated[0].id : null);
      setMessages(updated.length ? updated[0].messages : []);
    }
  };

  // File selection (multiple)
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const items = files.map(f => ({
      file: f,
      previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      kind: f.type.startsWith("image/") ? "image" : "file",
      name: f.name
    }));
    setUploadedFiles(prev => [...prev, ...items]);
    // reset input
    e.target.value = null;
  };

  const removeUploadedFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Actions click (from bot)
  const handleActionClick = async (action) => {
    if (action.type === 'button' && action.action === 'send_email_request') {
      try {
        const res = await fetch(buildApiUrl('/api/chat/action/send-email-request'), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId,
            sessionId, // Required for n8n Memo Memory node
            action: action.action,
            note: "User requested consultant verification"
          }),
        });
        const resultText = await res.text();

        // Add system confirmation as a bot message in the chat
        const confirmMsg = {
          id: Date.now(),
          text: "📧 تم إرسال الطلب للمستشار للتأكد من نظامية وصحة الرد.",
          isUser: false,
          sender: t('bot'),
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, confirmMsg]);
        setConversations(prev =>
          prev.map(c =>
            c.id === currentConversationId
              ? { ...c, messages: [...(c.messages || []), confirmMsg] }
              : c
          )
        );

        // (Optional) you can log resultText if needed
        console.log("send_email_request:", resultText);
      } catch (err) {
        console.error("❌ Error sending action:", err);
        const failMsg = {
          id: Date.now(),
          text: "❌ حدث خطأ أثناء إرسال الطلب للمستشار.",
          isUser: false,
          sender: t('bot'),
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, failMsg]);
        setConversations(prev =>
          prev.map(c =>
            c.id === currentConversationId
              ? { ...c, messages: [...(c.messages || []), failMsg] }
              : c
          )
        );
      }
    }
  };

  // Message toolbar functions
  const handleCopyMessage = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log('Message copied to clipboard');
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleLikeMessage = (messageId) => {
    console.log('Liked message:', messageId);
    // You could implement like functionality here
  };

  const handleDislikeMessage = (messageId) => {
    console.log('Disliked message:', messageId);
    // You could implement dislike functionality here
  };

  const handleShareMessage = (text) => {
    if (navigator.share) {
      navigator.share({
        title: 'Chat Message',
        text: text,
      }).catch(console.error);
    } else {
      // Fallback: copy to clipboard
      handleCopyMessage(text);
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (inputValue.trim() === '' && uploadedFiles.length === 0) return;

    setIsLoading(true);

    let convId = currentConversationId;
    // Create conversation if none
    if (!convId) {
      const title = inputValue.trim().slice(0, 30) || "New Chat";
      const newConv = {
        id: Date.now(),
        title,
        createdAt: new Date().toISOString(),
        messages: []
      };
      setConversations([newConv, ...conversations]);
      convId = newConv.id;
      setCurrentConversationId(convId);
      setMessages([]);
    }

    // Create user message
    const userMsg = {
      id: Date.now(),
      text: inputValue,
      files: uploadedFiles, // keep list for rendering
      isUser: true,
      sender: userId,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);

    // Save to conversation
    setConversations(prev =>
      prev.map(c => {
        if (c.id === convId) {
          const updatedMessages = [...(c.messages || []), userMsg];
          // set title from first message
          const newTitle = c.messages?.length === 0
            ? (inputValue.trim().slice(0, 30) || c.title)
            : c.title;
          return { ...c, title: newTitle, messages: updatedMessages };
        }
        return c;
      })
    );

    // reset input
    const originalUserMessage = inputValue;
    setInputValue('');
    setUploadedFiles([]);

    // Prepare formData (sent to Ydim backend; backend validates and forwards to AI webhook)
    const formData = new FormData();
    formData.append('userId', userId);
    formData.append('sessionId', sessionId);
    formData.append('message', userMsg.text || "");

    if (process.env.NODE_ENV === 'development' && isFirmMode) {
      console.log('[chat-send] activeFirmCase keys', activeFirmCase ? Object.keys(activeFirmCase) : null);
      console.log('[chat-send] activeFirmCase', activeFirmCase);
    }

    const firmCtx =
      isFirmMode && activeFirmCase ? buildFirmCaseContextPayload(activeFirmCase) : null;
    if (firmCtx) {
      const firmJson = JSON.stringify(firmCtx);
      if (process.env.NODE_ENV === 'development') {
        console.log('[chat-send] firmCaseContext JSON keys', Object.keys(firmCtx));
        console.log('[chat-send] firmCaseContext appended', firmCtx);
      }
      formData.append('firmCaseContext', firmJson);
    }

    // Use a stable multipart field name so backend forwarding + n8n mapping stay predictable.
    userMsg.files.forEach((fObj) => {
      formData.append('files', fObj.file, fObj.name);
    });

    const usedFirmContextForThisRequest = !!(isFirmMode && firmCtx);

    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.CHAT_SEND), {
        method: "POST",
        body: formData,
      });
      const headerBillingUi = parseBillingHeader(response);

      // Try JSON first
      let parsed = {};
      let replyText = "";
      let actions = [];
      let serverBillingUi = null;
      try {
        parsed = await response.json();
        replyText = extractAssistantReplyText(parsed);
        actions = extractWebhookActions(parsed);
        serverBillingUi = extractWebhookBilling(parsed, headerBillingUi);
      } catch {
        // fallback text
        const asText = await response.text();
        replyText = asText || " ";
        actions = [];
        serverBillingUi = headerBillingUi;
      }

      if (!response.ok) {
        setBillingUi(serverBillingUi);
        if (response.status === 403) {
          const msg =
            (parsed &&
              typeof parsed === 'object' &&
              !Array.isArray(parsed) &&
              parsed.message) ||
            'تم بلوغ حد الاستخدام. سجّل الدخول أو رقّ خطتك للمتابعة.';
          const hardBlockMsg = {
            id: Date.now(),
            text: msg,
            isUser: false,
            sender: t('bot'),
            timestamp: new Date(),
            paywallHardBlocked: true,
          };
          setMessages((prev) => [...prev, hardBlockMsg]);
          setConversations((prev) =>
            prev.map((c) =>
              c.id === convId ? { ...c, messages: [...(c.messages || []), hardBlockMsg] } : c
            )
          );
        }
        setIsLoading(false);
        return;
      }

      // Create bot placeholder (typing)
      const botMessageId = Date.now();
      const preview = buildPaywallPreviewParts(replyText, serverBillingUi);
      const botResponse = {
        id: botMessageId,
        text: "",
        actions,
        showActions: false, // 👈 لا نعرض الأزرار إلا بعد اكتمال الكتابة
        isUser: false,
        sender: t('bot'),
        timestamp: new Date(),
        usedFirmContext: usedFirmContextForThisRequest,
        paywallPreview: preview.isPreview,
        paywallHiddenPart: preview.hiddenPart || '',
      };
      setMessages(prev => [...prev, botResponse]);

      // تم إلغاء تأثير الكتابة التدريجي لتجنب تجمد الواجهة وضمان استمرار العملية في الخلفية
      setMessages(prev =>
        prev.map(m =>
          m.id === botMessageId
            ? { ...m, text: preview.visiblePart, showActions: true }
            : m
        )
      );
      setBillingUi(serverBillingUi);

      // إظهار التنبيهات
      setShowPredictionToast(true);
      setHighlightAdvancedBtn(true);
      setTimeout(() => {
        setShowPredictionToast(false);
        setHighlightAdvancedBtn(false);
      }, 6000);

      // حفظ المحادثة فوراً لضمان بقائها عند التنقل بين الصفحات
      setConversations(prev =>
        prev.map(c =>
          c.id === convId
            ? {
                ...c,
                messages: (c.messages || []).map(m =>
                  m.id === botMessageId ? { ...m, text: preview.visiblePart, showActions: true } : m
                )
              }
            : c
        )
      );

      // تتبع التفاعل (الحدود تُحسب على الخادم)
      trackInteraction(originalUserMessage, replyText, 'chat', user ? user.id : null);

      setIsLoading(false); // إنهاء حالة التحميل فور وصول الرد

      // also persist placeholder (initially empty text)
      setConversations(prev =>
        prev.map(c =>
          c.id === convId ? { ...c, messages: [...(c.messages || []), botResponse] } : c
        )
      );
    } catch (error) {
      console.error("❌ Fetch error:", error);
      const errorMessage = {
        id: Date.now(),
        text: "❌ حدث خطأ أثناء الاتصال بالخادم",
        isUser: false,
        sender: t('bot'),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setConversations(prev =>
        prev.map(c =>
          c.id === convId ? { ...c, messages: [...(c.messages || []), errorMessage] } : c
        )
      );
    }

    setIsLoading(false);
  };

  return (
    <div className={`unified-page ${isRTL ? 'rtl' : 'ltr'}`}>

      {/* Mobile Overlay */}
      {isMobile && (
        <div
          className={`sidebar-overlay ${isSidebarOpen ? 'open' : ''}`}
          onClick={handleOverlayClick}
        />
      )}

      {/* Sidebar */}
      <div ref={sidebarRef} className={`unified-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <button className="new-chat-btn" onClick={createNewConversation}>
            <span className="plus-icon">+</span> {t('newChat')}
          </button>
          <button className="sidebar-toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
            <span className="arrow-icon">{isSidebarOpen ? '←' : '→'}</span>
          </button>
        </div>

        <div className="conversations-list">
          {conversations.map(conversation => (
            <div
              key={conversation.id}
              className={`conversation-item ${currentConversationId === conversation.id ? 'active' : ''}`}
              onClick={() => loadConversation(conversation.id)}
            >
              <div className="conversation-content">
                <div className="conversation-title">{conversation.title}</div>
                <div className="conversation-date">{formatDate(conversation.createdAt)}</div>
              </div>
              <button
                className="delete-conversation-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteConversation(conversation.id);
                }}
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Main Chat */}
      <div className={`unified-main ${isSidebarOpen ? 'with-sidebar' : 'full-width'}`}>

        <div className="unified-header">
          <button className="mobile-sidebar-toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>☰</button>
          <h2>{t('Memorandum')}</h2>
        </div>
        {billingUi?.softGate?.active && !billingUi?.firmModeExempt && (
          <div className="soft-paywall-banner">
            <span>{billingUi?.softGate?.message || 'Usage limit reached for current plan.'}</span>
            <div className="soft-paywall-actions">
              {(!user || user.isGuest) ? (
                <>
                  <button type="button" onClick={() => navigate('/login')}>تسجيل الدخول</button>
                  <button type="button" onClick={() => navigate('/register')}>إنشاء حساب</button>
                  <button type="button" onClick={() => navigate('/pricing')}>الاطلاع على الأسعار</button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => setShowUpgradeModal(true)}>ترقية الخطة</button>
                  <button type="button" onClick={() => navigate('/pricing')}>عرض الباقات</button>
                </>
              )}
            </div>
          </div>
        )}

        <div className="unified-messages" ref={chatMessagesRef}>
        <div className="case-journey">
  {t('journey.chat')}
</div>


          {/* Memorandum instructions – shown only when chat is empty */}
{messages.length === 0 && (
  <div className="chat-instructions memorandum-instructions">
   {t('chat.instructions.memorandum')}
  </div>
)}

          {messages.map(message => (
            <div key={message.id} className={`message ${message.isUser ? 'user-message' : 'bot-message'}`}>
              <div className="message-content">
                {message.text && (
                  <div
                    className="message-text"
                    data-soft-preview={message.paywallPreview ? 'true' : 'false'}
                    style={{
                      direction: detectDirection(
                        `${message.text}${message.paywallHiddenPart || ''}`
                      ),
                      textAlign:
                        detectDirection(`${message.text}${message.paywallHiddenPart || ''}`) ===
                        'rtl'
                          ? 'right'
                          : 'left',
                    }}
                  >
                    {formatMessageText(message.text)}
                    {message.paywallPreview && message.paywallHiddenPart ? (
                      <div
                        className="paywall-preview-remainder"
                        aria-hidden="true"
                      >
                        {formatMessageText(message.paywallHiddenPart)}
                      </div>
                    ) : null}
                    {message.paywallPreview ? <InlinePaywallPreviewCard /> : null}
                    {message.paywallHardBlocked ? <InlinePaywallPreviewCard variant="hard" /> : null}
                  </div>
                )}

                {/* Files inside message (for user messages) */}
                {Array.isArray(message.files) && message.files.length > 0 && (
                  <div className="message-files">
                    {message.files.map((f, idx) => (
                      <div key={idx}>
                        {f.kind === "image" ? (
                          <img src={f.previewUrl} alt={f.name} className="chat-image" />
                        ) : (
                          <div className="file-message">📎 {f.name}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Actions AFTER typing only */}
                {message.actions && message.actions.length > 0 && message.showActions && (
                  <div className="message-actions">
                    {message.actions.map((action, idx) => (
                      <button
                        key={idx}
                        className="action-button"
                        onClick={() => handleActionClick(action)}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Message toolbar for assistant messages only */}
                {!message.isUser && message.text && (
                  <div className="message-toolbar">
                    <button
                      className="toolbar-button"
                      onClick={() => handleCopyMessage(message.text)}
                      title={t('copyMessage')}
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      className="toolbar-button"
                      onClick={() => handleLikeMessage(message.id)}
                      title={t('likeMessage')}
                    >
                      <ThumbsUp size={16} />
                    </button>
                    <button
                      className="toolbar-button"
                      onClick={() => handleDislikeMessage(message.id)}
                      title={t('dislikeMessage')}
                    >
                      <ThumbsDown size={16} />
                    </button>
                    <button
                      className="toolbar-button"
                      onClick={() => handleShareMessage(message.text)}
                      title={t('shareMessage')}
                    >
                      <Share size={16} />
                    </button>

                    {/* ✅ NEW: Advanced analysis button (ADDED ONLY) */}
                    <button
                     className={`toolbar-button ${highlightAdvancedBtn ? 'highlight-pulse' : ''}`}
                     onClick={() => {
                        setSelectedMessageText(message.text);
                        setIsAdvancedModalOpen(true);
                        setHighlightAdvancedBtn(false); // يختفي التنبيه عند الضغط
                      }}
                    title="تحليل متقدم"
                    >
                     🔍
                    </button>

                  </div>
                )}

                <div className="message-time">{formatTime(message.timestamp)}</div>
                {!message.isUser && message.usedFirmContext && (
                  <div className="firm-context-used-indicator" aria-hidden="true">
                    Firm case context was used for this message
                  </div>
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="message bot-message">
              <div className="message-content">
                <div className="typing-indicator"><span></span><span></span><span></span></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Floating scroll to bottom button */}
        {showScrollButton && (
          <button
            className="scroll-to-bottom-btn"
            onClick={manualScrollToBottom}
            title={t('scrollToBottom')}
          >
            ⬇️
          </button>
        )}
        {/* 🔔 Prediction Suggestion Toast */}
{showPredictionToast && (
  <div className="prediction-toast">
    <div className="prediction-toast-content">
      <strong>✨ خطوة إضافية متاحة</strong>
      <p>
        يمكنك الآن توقع احتمالية نجاح اللائحة والطلبات بناءً على التحليل.
      </p>
      <button
        className="prediction-toast-btn"
        onClick={() => {
          setShowPredictionToast(false);
          setIsAdvancedModalOpen(true);
        }}
      >
        توقع النتيجة
      </button>
    </div>
  </div>
)}



        <FirmCaseContextBar />

        {/* Input Area */}
        <form className="unified-input-form" onSubmit={handleSendMessage}>
          {uploadedFiles.length > 0 && (
            <div className="uploaded-preview">
              {uploadedFiles.map((f, idx) => (
                <div key={idx} className="preview-item">
                  {f.kind === "image" ? (
                    <img src={f.previewUrl} alt={f.name} className="preview-image" />
                  ) : (
                    <div className="preview-file">📎 {f.name}</div>
                  )}
                  <button type="button" className="remove-file" onClick={() => removeUploadedFile(idx)}>✕</button>
                </div>
              ))}
            </div>
          )}

          <div className="input-container">
            <label htmlFor="file-upload" className="file-upload-btn">+</label>
            <input id="file-upload" type="file" multiple onChange={handleFileChange} style={{ display: "none" }} />
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t('typeMessage')}
              className="chat-input"
              rows={1}
              style={{
                direction: detectDirection(inputValue),
                textAlign: detectDirection(inputValue) === 'rtl' ? 'right' : 'left'
              }}
            />
            <button type="submit" className="send-button" disabled={isLoading || (inputValue.trim() === '' && uploadedFiles.length === 0)}>
              {t('send')}
            </button>
          </div>
        </form>

        {/* ✅ NEW: Modal mount (ADDED ONLY) */}
        {isAdvancedModalOpen && (
          <div
            className="modal-overlay"
            onClick={() => setIsAdvancedModalOpen(false)}
            role="presentation"
          >
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              role="presentation"
            >
              <div className="modal-header">
                <h3>تحليل متقدم للرد</h3>
                <button
                  type="button"
                  className="modal-close-btn"
                  onClick={() => setIsAdvancedModalOpen(false)}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <AdvancedAnalysisModal
                text={selectedMessageText}
                userId={userId}
                sessionId={sessionId}
                firmCaseContext={
                  isFirmMode && activeFirmCase ? buildFirmCaseContextPayload(activeFirmCase) : null
                }
                onClose={() => setIsAdvancedModalOpen(false)}
              />
            </div>
          </div>
        )}
        {showUpgradeModal && user && !user.isGuest && (
          <UpgradeModal
            planId="pro"
            onClose={() => setShowUpgradeModal(false)}
            onSuccess={() => setShowUpgradeModal(false)}
          />
        )}
      </div>
    </div>
  );
};

/* ✅ NEW: Modal component (ADDED ONLY) */
/* ✅ UPDATED: AdvancedAnalysisModal (UI-enhanced, Non-breaking) */
const AdvancedAnalysisModal = ({ text, userId, sessionId, firmCaseContext, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState('');
  const [analysis, setAnalysis] = useState(null); // ✅ NEW (added only)

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      try {
        const res = await fetch(buildApiUrl('/api/chat/advanced-analysis'), {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId,
            sessionId,
            originalAnswer: text,
            ...(firmCaseContext ? { firmCaseContext } : {}),
          }),
        });

        const data = await res.json();

        // ✅ NEW: safe parsing (without breaking old behavior)
        let parsed = null;
        if (typeof data === 'string') {
          try {
            parsed = JSON.parse(data);
          } catch {
            parsed = null;
          }
        } else {
          parsed = data;
        }

        if (isMounted) {
          setResult(JSON.stringify(parsed, null, 2)); // keep original text (fallback)
          setAnalysis(parsed); // ✅ structured data
        }
      } catch (err) {
        if (isMounted) {
          setResult("❌ فشل التحليل");
          setAnalysis(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      isMounted = false;
    };
  }, [text, userId, sessionId]);

  return (
    <div className="modal-body" dir="rtl">
      {loading ? (
        <p>⏳ جاري التحليل...</p>
      ) : analysis ? (
        /* ✅ NEW: Professional Judicial Analysis UI */
        <div className="analysis-ui">
          <h4>⚖️ التحليل القضائي المتقدم</h4>

          <div className="analysis-grid">
            <div><strong>نوع النزاع:</strong> {analysis.legal_domain}</div>
            <div><strong>الجهة القضائية:</strong> {analysis.court_type}</div>
            <div><strong>درجة اليقين:</strong> {analysis.certainty_level}</div>
          </div>

          <div className="analysis-section">
            <strong>📈 احتمالية النتيجة</strong>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${analysis.outcome_probability || 0}%` }}
              />
            </div>
            <span>{analysis.outcome_probability}%</span>
          </div>

          <div className="analysis-section">
            <strong>⚠️ المخاطر القضائية</strong>
            <span className={`risk ${analysis.risk_level}`}>
              {analysis.risk_level} ({analysis.judicial_risk_index})
            </span>
          </div>

          <div className="analysis-section">
            <strong>🔍 العوامل المؤثرة</strong>
            <ul>
              {Array.isArray(analysis.key_factors) &&
                analysis.key_factors.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
            </ul>
          </div>

          <div className="analysis-section recommendation">
            <strong>🧠 التوصية القانونية</strong>
            <p>{analysis.judicial_recommendation}</p>
          </div>
        </div>
      ) : (
        /* ✅ fallback (kept for safety) */
        <div className="modal-result">
          <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{result}</pre>
        </div>
      )}

      <div className="modal-actions">
        <button type="button" className="modal-close-action" onClick={onClose}>
          إغلاق
        </button>
      </div>
    </div>
  );
};
export default ChatPage;
